import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, Button, Badge, Spinner } from 'react-bootstrap'
import { Document, Page, pdfjs } from 'react-pdf'
import { baseUrl } from 'api/helpers'
import { formatDateLocal } from 'helpers/datetime'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

const ViewReceiptModal = ({ show, onHide, receiptScan, scanType = 'receipt' }) => {
  const isInvoice = scanType === 'invoice' || receiptScan?.scan_type === 'invoice'
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [pdfError, setPdfError] = useState(null)
  const [pdfScale, setPdfScale] = useState(1.0)

  if (!receiptScan) return null

  const imageUrl = receiptScan.receipt_image_url
    ? `${baseUrl}${receiptScan.receipt_image_url}`
    : null
  const isPdf = receiptScan.file_name?.toLowerCase().endsWith('.pdf')

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const handleDownload = () => {
    window.open(imageUrl, '_blank')
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setPdfLoading(false)
    setPdfError(null)
  }

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error)
    setPdfLoading(false)
    setPdfError('Failed to load PDF')
  }

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages))
  }

  const zoomIn = () => {
    setPdfScale((prev) => Math.min(prev + 0.25, 2.5))
  }

  const zoomOut = () => {
    setPdfScale((prev) => Math.max(prev - 0.25, 0.5))
  }

  const resetZoom = () => {
    setPdfScale(1.0)
  }

  const getMatchTypeBadge = (matchType) => {
    if (!matchType) return null
    const typeMap = {
      auto: { className: 'badge-success', text: 'Auto Matched' },
      manual: { className: 'badge-primary', text: 'Manually Matched' }
    }
    const config = typeMap[matchType] || { className: 'badge-secondary', text: matchType }
    return <Badge bg="" className={config.className}>{config.text}</Badge>
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isInvoice ? 'Invoice' : 'Receipt'}
          {receiptScan.match_type && (
            <span className="ms-3">
              {getMatchTypeBadge(receiptScan.match_type)}
              {receiptScan.match_confidence !== null && (
                <Badge bg="" className="badge-info ms-2">
                  {Math.round(receiptScan.match_confidence * 100)}% confidence
                </Badge>
              )}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: '#f8f9fa', minHeight: '60vh' }}>
        {/* Extracted Data (effective = user override or OCR) */}
        {(receiptScan.effective_vendor_name || receiptScan.effective_total_amount) && (
          <div className="bg-white p-3 rounded mb-3 shadow-sm">
            <h6 className="mb-3">Extracted Information</h6>
            <div className="row">
              {receiptScan.effective_vendor_name && (
                <div className="col-md-3 mb-2">
                  <small className="text-muted d-block">Vendor</small>
                  <span>{receiptScan.effective_vendor_name}</span>
                </div>
              )}
              {receiptScan.effective_total_amount && (
                <div className="col-md-3 mb-2">
                  <small className="text-muted d-block">Amount</small>
                  <span>{formatCurrency(receiptScan.effective_total_amount)}</span>
                </div>
              )}
              {receiptScan.effective_transaction_date && (
                <div className="col-md-3 mb-2">
                  <small className="text-muted d-block">Date</small>
                  <span>{formatDateLocal(receiptScan.effective_transaction_date)}</span>
                </div>
              )}
              {isInvoice ? (
                <>
                  {receiptScan.effective_invoice_number && (
                    <div className="col-md-3 mb-2">
                      <small className="text-muted d-block">Invoice #</small>
                      <span>{receiptScan.effective_invoice_number}</span>
                    </div>
                  )}
                  {receiptScan.effective_po_number && (
                    <div className="col-md-3 mb-2">
                      <small className="text-muted d-block">PO #</small>
                      <span>{receiptScan.effective_po_number}</span>
                    </div>
                  )}
                  {receiptScan.effective_due_date && (
                    <div className="col-md-3 mb-2">
                      <small className="text-muted d-block">Due Date</small>
                      <span>{formatDateLocal(receiptScan.effective_due_date)}</span>
                    </div>
                  )}
                </>
              ) : (
                receiptScan.effective_card_last_four && (
                  <div className="col-md-3 mb-2">
                    <small className="text-muted d-block">Card Last 4</small>
                    <span>****{receiptScan.effective_card_last_four}</span>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Receipt Image/PDF */}
        <div className="text-center">
          {imageUrl ? (
            isPdf ? (
              <div>
                {pdfLoading && (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <span className="ms-2">Loading PDF...</span>
                  </div>
                )}

                {pdfError && (
                  <div className="text-center py-5">
                    <p className="text-danger mb-3">{pdfError}</p>
                    <Button variant="primary" onClick={handleDownload}>
                      Open PDF in New Tab
                    </Button>
                  </div>
                )}

                <div style={{ overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto' }}>
                  <Document
                    file={imageUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading=""
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={pdfScale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                  </Document>
                </div>

                {!pdfLoading && !pdfError && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-3 flex-wrap">
                    {/* Zoom Controls */}
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={zoomOut}
                        disabled={pdfScale <= 0.5}
                        title="Zoom out"
                      >
                        −
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        onClick={resetZoom}
                        title="Reset zoom"
                        style={{ minWidth: '60px' }}
                      >
                        {Math.round(pdfScale * 100)}%
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        onClick={zoomIn}
                        disabled={pdfScale >= 2.5}
                        title="Zoom in"
                      >
                        +
                      </Button>
                    </div>

                    {/* Page Controls */}
                    {numPages && numPages > 1 && (
                      <div className="d-flex align-items-center gap-1">
                        <Button
                          variant="light"
                          size="sm"
                          onClick={goToPrevPage}
                          disabled={pageNumber <= 1}
                        >
                          Prev
                        </Button>
                        <span className="text-muted small">
                          {pageNumber} / {numPages}
                        </span>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={goToNextPage}
                          disabled={pageNumber >= numPages}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <img
                src={imageUrl}
                alt="Receipt"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            )
          ) : (
            <div className="py-5 text-muted">No receipt image available</div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Close
        </Button>
        {imageUrl && (
          <Button variant="primary" onClick={handleDownload}>
            Download
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

ViewReceiptModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  receiptScan: PropTypes.object,
  scanType: PropTypes.string
}

export default ViewReceiptModal
